import {
  Component,
  computed,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';

import { DataLayerService } from '../../../services/DataLayer/data-layer.service';
import { MembershipService } from '../../../shared/services/membership.service';
import { selectCotizacion } from '../../../storage/Cotizacion/cotizacion.selectors';
import { selectData } from '../../../storage/Lead/data.selectors';
import { selectPlanes } from '../../../storage/Planes/planes.selector';

import { esAltoCilindraje } from '../../../helpers/cilindraje';
import {
  etiquetasCoberturasOpcionales,
  etiquetasCoberturasResumen,
} from '../../../helpers/coberturasResumen';
import { getPlanesData, getVehicleData } from '../../../helpers/dataLayerHelpers';
import { getSessionId } from '../../../helpers/storageUtils';

import { tarifaCoberturaOpcional } from '../../../Models/coberturaOpcional';
import { Product } from '../../../Models/planes';

import { CommonModule } from '@angular/common';
import { catchError, of, switchMap, take, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Cotizacion } from '../../../Models/cotizacion';
import { CrearCotizacion } from '../../../Models/crearCotizacion';
import { DatosGenerales } from '../../../Models/datosGenerales';
import { DatosPlanes } from '../../../Models/datosPlanes';
import { StepperComponent } from '../../../movilidad/pages/stepper/stepper.component';
import { PipesModule } from '../../../pipes/pipes.module';
import {
  PAGO_APROBADO_NO_AP,
  PAGO_RECHAZADO,
  POLIZA_ACTIVA,
  PROCESANDO_PAGO,
  REDIRECT,
  YATEMENOSDATOS
} from '../../../routes.constans';
import { CotizacionService } from '../../../services/Cotizacion/cotizacion.service';
import { ExceptionService } from '../../../services/excepciones/exception.service';
import { InactivityService } from '../../../services/inactivity/inactivity.service';
import { UtilsService } from '../../../services/utils.service';
import { InspectionAlertComponent } from '../../../shared/components/inspection-alert/inspection-alert.component';
import { ManualPaymentCardComponent } from '../../../shared/components/manual-payment-card/manual-payment-card.component';
import { MobilityLoaderComponent } from '../../../shared/components/mobility-loader/mobility-loader.component';
import { SummaryPlanCardComponent } from '../../../shared/components/new-summary-plan-card/summary-plan-card.component';
import { PaymentSummaryComponent } from '../../../shared/components/payment-summary/payment-summary.component';
import { PolicyStartDateComponent } from '../../../shared/components/policy-start-date/policy-start-date.component';
import { SubscriptionCardComponent } from '../../../shared/components/subscription-card/subscription-card.component';
import { TokenService } from '../../../shared/services/token.service';
import { loadCotizacion } from '../../../storage/Lead/data.actions';
import { BannerAppComponent } from '../../banner-app/banner-app.component';
import { FooterAppComponent } from '../../footer-app/footer-app.component';
import { MensajeResumenComponent } from '../../mensaje-resumen/mensaje-resumen.component';

@Component({
  selector: 'app-resumen-compra',
  standalone: true,
  imports: [
    CommonModule,
    PipesModule,
    FooterAppComponent,
    BannerAppComponent,
    StepperComponent,
    MensajeResumenComponent,
    SummaryPlanCardComponent,
    MobilityLoaderComponent,
    InspectionAlertComponent,
    PaymentSummaryComponent,
    PolicyStartDateComponent,
    SubscriptionCardComponent,
    ManualPaymentCardComponent,
  ],
  templateUrl: './resumen-compra.component.html',
  styleUrls: ['./resumen-compra.component.scss'],
})
export class ResumenCompraComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly membershipService = inject(MembershipService);
  private readonly dataLayerService = inject(DataLayerService);
  private readonly exceptionService = inject(ExceptionService);
  private readonly utils = inject(UtilsService);
  private readonly cotizacionService = inject(CotizacionService);
  private readonly TokenService = inject(TokenService);
  private readonly inactivityService = inject(InactivityService);

  // --- SEÑALES DE ESTADO LOCAL (Reemplazan la dependencia directa del store) ---
  // Estas señales almacenarán los datos, ya sea del store o de la API.
  private data$: WritableSignal<DatosGenerales | null> = signal(null); // Para datos generales y del vehículo
  private planData$: WritableSignal<DatosPlanes | null> = signal(null); // Para datos de planes y pagos
  private cotizacionData$: WritableSignal<CrearCotizacion | null> = signal(null);

  private inactivityTimer: any;
  private readonly INACTIVITY_TIMEOUT = environment.LOCAL_TIMER || 5 * 60 * 1000; // Usar LOCAL_TIMER del environment o 5 minutos por defecto

  private readonly datosGeneralesFromStore = toSignal(this.store.select(selectData));
  private readonly datosPlanesFromStore = toSignal(this.store.select(selectPlanes));
  private readonly datosCotizacionFromStore = toSignal(this.store.select(selectCotizacion));
  readonly isPseDisabled = signal(
    Date.now() >= new Date(environment.pseDisabledOptionStartDate).getTime() &&
    Date.now() <= new Date(environment.pseDisabledOptionEndDate).getTime()
  );

  // --- SEÑALES PÚBLICAS Y DE ESTADO ---

  readonly fecha = computed<Date>(() => {
    const bogotaNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Bogota' }));
    const bogotaToday = new Date(bogotaNow.getFullYear(), bogotaNow.getMonth(), bogotaNow.getDate());

    const effectiveDateStr = this.cotizacionData$()?.Cotizacion?.data?.plan?.effectiveDate;

    if (effectiveDateStr) {
      const match = effectiveDateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
      const effectiveDate = match
        ? new Date(+match[1], +match[2] - 1, +match[3])
        : new Date(effectiveDateStr);

      if (!isNaN(effectiveDate.getTime()) && effectiveDate >= bogotaToday) {
        return effectiveDate;
      }
    }

    return bogotaNow;
  });
  readonly isPaying = signal(false);
  readonly selectedPaymentMethod = signal<'subscription' | 'payment' | null>(null);
  readonly showPromo = signal(true);
  readonly promoPoints = signal<number>(5);
  readonly pointsPerAmount = signal<number>(800);
  readonly promoBannerImage = signal<string>('');
  private codAsesor = signal('');
  private sessionId = signal('');
  readonly isLoading = signal(false);
  readonly showBackButton = signal(true);

  readonly tipoPago = computed(() => this.planData$()?.payment_frequency);
  private leadResponse = computed(() => this.data$()?.LeadResponse?.data);
  vehicle = computed(() => this.leadResponse()?.vehicle);
  private owner = computed(() => this.vehicle()?.owner);

  readonly vehicleDescription = computed(() => {
    const v = this.vehicle();
    if (!v) return '';
    return `${v.brand} ${v.model} ${v.cylinder} CC`;
  });

  readonly ownerFullName = computed(() => {
    const o = this.owner();
    if (!o) return '';
    return `${o.firstnames} ${o.lastnames}`;
  });

  readonly ownerDocument = computed(() => this.owner()?.document?.number || '');

  readonly ownerInfo = computed(() => {
    return {
      fullname: this.ownerFullName(),
      document: this.owner()?.document || { type: '', number: '' },
    };
  });

  readonly pagoPlan = computed<Product | undefined>(() => {
    const planes = this.planData$()?.planes;
    const selectedPlanCode = this.planData$()?.selected_plan;
    if (!planes || !selectedPlanCode) return undefined;
    return planes.data.products.find((plan: Product) => plan.code === selectedPlanCode);
  });

  readonly coveragesIncluded = computed(() => {
    return this.pagoPlan()?.coveragesGroup?.filter((coverage) => coverage.include) ?? [];
  });

  /** La moto es de alto cilindraje, segun los datos del lead o de la cotizacion. */
  readonly esAltoCilindraje = computed(() => esAltoCilindraje(this.vehicle()?.cylinder));

  /** Coberturas incluidas, con el texto y el orden del resumen. */
  readonly etiquetasCoberturas = computed(() =>
    etiquetasCoberturasResumen(this.pagoPlan()?.coveragesGroup, this.esAltoCilindraje()),
  );

  /** Coberturas opcionales que el usuario activo en la pantalla de planes. */
  readonly etiquetasOpcionales = computed(() =>
    etiquetasCoberturasOpcionales(this.planData$()?.coberturasOpcionales),
  );

  readonly cuidaCirculacion = computed(() => {
    const zona = this.cotizacionData$()?.CotizacionEnvio?.zona_circulacion ?? '';
    if (!zona) return '';
    return zona.charAt(0).toUpperCase() + zona.slice(1).toLowerCase();
  });

  /**
   * Prima de las coberturas opcionales activas en la frecuencia indicada.
   * Devuelve 0 si el backend no envio sus tarifas o si no hay ninguna activa.
   */
  private valorOpcionales(frecuencia: 'Monthly' | 'Annually'): number {
    const tarifas = this.pagoPlan()?.coberturasOpcionales;
    if (!tarifas) return 0;

    return (this.planData$()?.coberturasOpcionales ?? []).reduce((total, codigo) => {
      const tarifa = tarifaCoberturaOpcional(tarifas, codigo);
      const precio = frecuencia === 'Monthly' ? tarifa?.priceMensual : tarifa?.priceAnual;
      return total + (Number(precio?.amount) || 0);
    }, 0);
  }

  /**
   * Valor del seguro por el ano completo, con las coberturas opcionales.
   * Es el que la tarjeta rotula como "Valor total del seguro".
   */
  readonly valorTotalSeguro = computed(() => {
    const plan = this.pagoPlan();
    if (!plan) return 0;

    return this.tipoPago() === 'Monthly'
      ? (plan.priceMensual?.amount ?? 0) + this.valorOpcionales('Monthly')
      : (plan.priceAnual?.amount ?? 0) + this.valorOpcionales('Annually');
  });

  readonly valorMes = computed(() => {
    const base = this.pagoPlan()?.priceMensual?.amount ?? 0;
    return base > 0 ? (base + this.valorOpcionales('Monthly')) / 12 : 0;
  });

  readonly valorAPagar = computed(() => {
    const plan = this.pagoPlan();
    if (!plan) return 0;
    if (this.tipoPago() === 'Monthly') {
      return this.valorMes();
    } else {
      return (plan.priceAnual?.amount ?? 0) + this.valorOpcionales('Annually');
    }
  });

  private queryParams$ = this.route.queryParams.pipe(
    takeUntilDestroyed(),
    tap((params) => this.codAsesor.set(params['codigoAsesor'] || '')),
    switchMap((params) => {
      const idCotizacion = params['prepolicyid'];

      if (idCotizacion) {
        const decodedId = atob(idCotizacion);
        this.isLoading.set(true);
        this.showBackButton.set(false);
        return this.TokenService.initializeToken().pipe(
          take(1),
          switchMap(() => {
            return this.cotizacionService.getCotizacion(this.sessionId(), decodedId).pipe(
              catchError((error) => {
                console.error('Error al obtener la cotización desde la URL:', error);
                this.utils.handleredirect(this.codAsesor(), REDIRECT);
                return of(null);
              }),
            );
          }),
        );
      } else {
        // Si no hay idCotizacion, continuamos como antes.
        return of(null);
      }
    }),
  );

  private inactivityTimerStarted = false;

  ngOnInit(): void {
    this.sessionId.set(getSessionId().toString());

    this.queryParams$.subscribe((response) => {
      let dataLoadedSuccessfully = false;
      if (response) {
        this.isLoading.set(false);
        dataLoadedSuccessfully = this.setDataFromApi(response);
      } else {
        this.setDataFromStore();
        dataLoadedSuccessfully = true;
      }
      if (dataLoadedSuccessfully) {
        this.pushDataLayer('Pageview');
        this.setupInactivityTimer();
      }
    });
  }

  // --- MÉTODOS DE TRANSFORMACIÓN Y ASIGNACIÓN DE DATOS ---

  /**
   * Transforma la respuesta del servicio de cotización y la asigna a las señales locales.
   * @param cotizacionResponse - La respuesta del servicio getCotizacion.
   *  @returns boolean - `true` si los datos son válidos, `false` si son incompletos.
   */
  private setDataFromApi(cotizacionResponse: Cotizacion): boolean {
    const data = cotizacionResponse?.data;

    if (!data || !data.asset || !data.plan || !data.quote_id) {
      console.error(
        'La información de la cotización es inválida o incompleta. Redirigiendo al inicio.',
        cotizacionResponse,
      );
      this.utils.handleredirect(this.codAsesor(), REDIRECT);
      return false; // Notifica que la carga falló
    }

    const { asset, plan, quote_id, lead_id } = cotizacionResponse.data;

    // La cotizacion no devuelve las coberturas opcionales contratadas ni su
    // tarifa, asi que se recuperan del store de planes. En una sesion nueva
    // (link reabierto, otro dispositivo) el store esta vacio y la cobertura se
    // pierde: por eso backend debe incluirlas en la respuesta.
    const planesDelStore = this.datosPlanesFromStore();
    const opcionalesContratadas = planesDelStore?.coberturasOpcionales ?? [];
    const tarifasOpcionales = planesDelStore?.planes?.data?.products?.find(
      (producto) => producto.code === planesDelStore?.selected_plan,
    )?.coberturasOpcionales;

    // 1. Mapeo para `data$`
    const [firstnames, ...lastnames] = (asset.owner.fullname || '').split(' ');
    this.data$.set({
      LeadResponse: {
        data: {
          vehicle: {
            brand: asset.brand,
            model: asset.model,
            cylinder: asset.cylinder,
            owner: {
              firstnames: firstnames || '',
              lastnames: lastnames.join(' ') || '',
              document: {
                number: asset.owner.document.number,
                type: asset.owner.document.type,
              },
            },
            Prenda: '',
            circulation_zone_id: '',
            plate: asset.plate || '',
            use_type: '',
          },
          Id: lead_id || '',
          lead: {
            accept_tos: false,
            mobile_phone: '',
            owner_borndate: '',
          },
        },
      },
      Lead: {
        assessor_code: 0,
        plate: asset.plate || '',
        confirm_plate: '',
        circulation_zone_id: '',
        cityName: plan.zonaCirculacion || '',
        use_type: '',
        accept_tos: false,
        name: '',
        mobile_phone: '',
      },
    });

    // 2. Mapeo para `planData$`
    // Creamos un objeto 'Product' a partir de los datos del plan
    const product: Product = {
      code: typeof plan.selected === 'string' ? plan.selected : plan.selected.Selected,
      priceAnual: {
        amount: plan.price.full.amount,
        currency: plan.price.full.currency,
        formatted: plan.price.full.formatted,
      },
      priceMensual: {
        amount: plan.price.full.amount,
        currency: plan.price.full.currency,
        formatted: plan.price.full.formatted,
      },
      coveragesGroup: plan.coveragesGroup ?? [],
      order: 0,
      name: typeof plan.selected === 'string' ? plan.selected : (plan.selected.Selected ?? ''),
      coverages: [],
      sumInsured: plan.sumInsured ?? undefined,
      coberturasOpcionales: tarifasOpcionales,
    };

    this.planData$.set({
      payment_frequency: plan.paymentFrecuency === 'Monthly' ? 'Monthly' : 'Annually',
      selected_plan: product.code,
      planes: {
        data: {
          products: [product], // El componente espera un array, lo creamos con el único plan
        },
        FalloTarifaEsencialMotos: false,
        FalloTarifaTotalMotos: false,
      },
      lead_id: '',
      coberturasOpcionales: opcionalesContratadas,
    });

    // 3. Mapeo para `cotizacionData$`
    this.cotizacionData$.set({
      Cotizacion: {
        data: {
          quote_id: quote_id,
          asset: asset,
          plan: plan,
          pre_policy_number: '',
        },
      },
      CotizacionEnvio: {
        zona_circulacion: plan.zonaCirculacion || '',
        selected_plan: '',
        lead_id: '',
        payment_frequency: '',
        coberturasOpcionales: opcionalesContratadas,
      },
      dueño_paga_seguro: 'Si', // Valor por defecto o buscarlo si viene en la respuesta
    });
    return true;
  }

  /**
   * Obtiene los datos del store y los asigna a las señales locales.
   */
  private setDataFromStore(): void {
    this.data$.set(this.datosGeneralesFromStore() ?? null);
    this.planData$.set(this.datosPlanesFromStore() ?? null);
    this.cotizacionData$.set(this.datosCotizacionFromStore() ?? null);
  }

  ngOnDestroy(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
  }

  handlePayment(membershipType: 'payment' | 'subscription'): void {
    // Prevenir múltiples clics
    if (this.isPaying()) {
      return;
    }

    this.isPaying.set(true);
    this.pushDataLayer('Button', 'Pagar');

    const quoteId = this.cotizacionData$()?.Cotizacion?.data?.quote_id;

    if (!quoteId) {
      console.error('No se encontró el quote_id para procesar el pago.');
      this.isPaying.set(false);
      return;
    }

    this.membershipService.getLightboxUrl(this.sessionId(), quoteId, membershipType).subscribe({
      next: (response) => {
        if (
          response?.data?.status?.status === 'PENDING' &&
          response?.data?.status?.message === 'Se encuentra un pago pentiente para otra cotización'
        ) {
          console.log('Pago en proceso, redirigiendo...');
          this.paymentRedirect(PAGO_RECHAZADO, quoteId);
        } else if (response?.data?.status?.status === 'APPROVED') {
          if (
            response?.data?.status?.message === 'Hay una sesión aprobada que tiene póliza Vigente.'
          ) {
            this.paymentRedirect(POLIZA_ACTIVA, quoteId);
          } else {
            console.log('Pago aprobado, redirigiendo a PAGO_APROBADO_NO_AP...');
            this.paymentRedirect(PAGO_APROBADO_NO_AP, quoteId);
          }
        } else if (response?.data?.processUrl) {
          const P = (window as any).P;
          if (P) {
            P.init(response.data.processUrl);
          } else {
            console.warn('Librería de pago "P" no encontrada');
          }
          P.on('close', () => {
            this.paymentRedirect(PROCESANDO_PAGO, quoteId);
          });
        } else {
          console.error('La respuesta del servicio no contiene una processUrl.');
          this.isPaying.set(false);
        }
      },
      error: (err) => {
        console.error('Error al obtener la URL de pago:', err);
        this.activateException('falla_técnica_pago', 75);
        this.paymentRedirect(YATEMENOSDATOS, quoteId);
        this.isPaying.set(false);
      },
    });
  }

  private pushDataLayer(ecom_item: 'Pageview' | 'Button', ecom_subitem = ''): void {
    this.dataLayerService.pushDataLayer({
      event: 'moto_ecommerce_step_5_resumen_de_compra',
      ecom_tipo: 'Moto',
      ecom_item,
      ecom_subitem,
      ecom_pantalla: 'Resumen de compra',
      user_id: this.sessionId(),
      ...getVehicleData(this.data$()),
      ...getPlanesData(this.planData$()),
      dueño_paga_seguro: this.cotizacionData$()?.dueño_paga_seguro ?? 'Si',
    });
  }

  @HostListener('document:mousemove')
  @HostListener('document:keypress')
  @HostListener('document:click')
  @HostListener('document:wheel')
  resetInactivityTimer() {
    clearTimeout(this.inactivityTimer);
    this.setupInactivityTimer();
  }

  private async setupInactivityTimer() {
    // await saveToStorageAsync('ruta-inactividad', 'resumen', 'session');
    // this.inactivityTimer = setTimeout(() => {
    //   this.activateException('Abandono_Resumen_Planes', 60);
    //   this.utils.handleredirect(this.codAsesor(), TIEMPO_INACTIVO);
    // }, this.INACTIVITY_TIMEOUT);
    if (this.inactivityTimerStarted) {
      return;
    }

    const inactivityData = this.data$();

    if (inactivityData) {
      this.store.dispatch(loadCotizacion({ data: inactivityData }));

      this.inactivityService.setLeadData(inactivityData);

      this.inactivityService.startDefaultTimerForScreen(
        'Resumen-compra',
        'Abandono Resumen de compra',
        '95',
      );
      this.inactivityTimerStarted = true;
    }
  }

  paymentRedirect(url: string, recordId: string) {
    this.isPaying.set(false);
    this.utils.handleredirect(this.codAsesor(), url, {
      queryParams: { prepolicyid: recordId },
    });
  }

  activateException(prospect: string, percentage: number): void {
    this.exceptionService
      .addExceptionNew(
        this.sessionId(),
        this.data$()?.LeadResponse.data.Id ?? '',
        prospect,
        percentage,
      )
      .subscribe({
        next: (resp) => {
          console.log('Se actualizo el porcentaje de la excepción');
        },
        error: (error) =>
          console.error('Error al actualizar el porcentaje de la excepción:', error),
      });
  }
}
